import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { CelebrationOverlay } from '../components/CelebrationOverlay';

const S = {
  page: { background: '#f4f6fb', minHeight: '100vh' },
  hero: { background: 'white', borderBottom: '1px solid #e5e7eb', padding: '18px 16px 14px', position: 'relative', overflow: 'hidden' },
  heroWatermark: { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '5rem', color: '#1E3A6E', opacity: 0.04, pointerEvents: 'none', userSelect: 'none' },
  heroDate: { fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 3 },
  heroTitle: { fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '2rem', color: '#1E3A6E', lineHeight: 1.1 },
  heroSub: { fontSize: '0.82rem', color: '#6b7280', marginTop: 4 },
  body: { padding: '0 16px' },
  sectionLabel: { fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6b7280' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 },
  card: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  statBox: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 12px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  statVal: { fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1.6rem', color: '#1E3A6E', lineHeight: 1, marginBottom: 4 },
  statLbl: { fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280' },
  actionTile: { background: 'white', border: '1px solid #e5e7eb', borderBottom: '3px solid #1E3A6E', borderRadius: 12, padding: '14px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  actionIcon: { fontSize: '1.5rem', lineHeight: 1 },
  actionLbl: { fontFamily: 'Rajdhani,sans-serif', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1E3A6E', textAlign: 'center' },
  spacer: (n) => ({ height: n }),
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
};

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Ground weather (next match) — Open-Meteo hourly, reporting time → 2 PM ──
const WX = (code) => {
  if (code == null) return { e: '🌡️', l: '—' };
  if (code === 0) return { e: '☀️', l: 'Clear' };
  if (code <= 2) return { e: '🌤️', l: 'Mainly clear' };
  if (code === 3) return { e: '☁️', l: 'Cloudy' };
  if (code === 45 || code === 48) return { e: '🌫️', l: 'Fog' };
  if (code >= 51 && code <= 57) return { e: '🌦️', l: 'Drizzle' };
  if (code >= 61 && code <= 67) return { e: '🌧️', l: 'Rain' };
  if (code >= 71 && code <= 77) return { e: '🌨️', l: 'Snow' };
  if (code >= 80 && code <= 82) return { e: '🌧️', l: 'Showers' };
  if (code >= 95) return { e: '⛈️', l: 'Storm' };
  return { e: '🌡️', l: '—' };
};
const wxHourLabel = (h) => {
  const ap = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr} ${ap}`;
};
function WeatherCard() {
  const [wx, setWx] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [openMetric, setOpenMetric] = useState(null); // 'feels' | 'wind' | 'humidity'
  useEffect(() => {
    let alive = true;
    api.get('/matches/upcoming-weather')
      .then(r => { if (alive) { setWx(r.data); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);
  if (!loaded || !wx || !wx.match) return null;
  const dateLabel = new Date(wx.match.match_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  const rep = wx.match.reporting_time ? String(wx.match.reporting_time).slice(0, 5) : null;
  const hasHourly = wx.hourly && wx.hourly.length;
  const maxRain = hasHourly ? Math.max(...wx.hourly.map(h => h.precip || 0)) : 0;
  const range = (key, suffix = '') => {
    if (!hasHourly) return null;
    const vals = wx.hourly.map(h => h[key]).filter(v => v != null);
    if (!vals.length) return null;
    const lo = Math.min(...vals), hi = Math.max(...vals);
    return lo === hi ? `${lo}${suffix}` : `${lo}–${hi}${suffix}`;
  };
  const feelsRange = range('feels', '°');
  const windRange = range('wind', '');
  const humRange = range('humidity', '%');
  const metricMeta = {
    feels: { label: 'FEELS', suffix: '°', key: 'feels' },
    wind: { label: 'WIND', suffix: '', key: 'wind' },
    humidity: { label: 'HUMIDITY', suffix: '%', key: 'humidity' },
  };
  const clockISO = (iso) => {
    const t = String(iso || '').slice(11, 16); if (!t) return null;
    const [hh, mm] = t.split(':').map(Number);
    const ap = hh < 12 ? 'AM' : 'PM'; const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, '0')} ${ap}`;
  };
  const fogNote = wx.fog && wx.fog.foggy
    ? (wx.fog.clearsAtHour != null ? `Fog likely, clearing ~${wxHourLabel(wx.fog.clearsAtHour)}` : 'Fog may persist into the morning')
    : null;
  const sunriseStr = wx.sunrise ? clockISO(wx.sunrise) : null;
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: '13px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: hasHourly ? 10 : 0 }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#1E3A6E' }}>{(wx?.venue || 'UCC Ground')} Weather · {dateLabel}{wx?.tz_differs ? ` · local time` : ''}</div>
        {rep && <div style={{ fontSize: '0.66rem', color: '#9aa7bd' }}>Report {rep}</div>}
      </div>
      {!hasHourly ? (
        <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Forecast will appear closer to match day.</div>
      ) : (
        <>
          {(sunriseStr || fogNote) && (
            <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {sunriseStr && <span>🌅 Sunrise {sunriseStr}</span>}
              {fogNote && <span style={{ color: '#b45309', fontWeight: 600 }}>🌫️ {fogNote}</span>}
            </div>
          )}
          {maxRain >= 40 && (
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: maxRain >= 60 ? '#dc2626' : '#b45309', marginBottom: 8 }}>
              ☔ Up to {maxRain}% chance of rain during play
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
            {wx.hourly.map((h, i) => {
              const w = WX(h.code);
              return (
                <div key={i} style={{ flex: '0 0 auto', textAlign: 'center', width: 48, background: '#f8fafc', border: '1px solid #eef1f6', borderRadius: 12, padding: '7px 4px' }}>
                  <div style={{ fontSize: '0.55rem', color: '#94a3b8', fontWeight: 600 }}>{wxHourLabel(h.hour)}</div>
                  <div style={{ fontSize: '1.1rem', lineHeight: 1.4 }} title={w.l}>{w.e}</div>
                  <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#1E3A6E' }}>{h.temp != null ? `${h.temp}°` : '—'}</div>
                  <div style={{ fontSize: '0.54rem', color: (h.precip || 0) >= 40 ? '#2563eb' : '#b4bdca', fontWeight: (h.precip || 0) >= 40 ? 600 : 400 }}>💧{h.precip != null ? `${h.precip}%` : '—'}</div>
                </div>
              );
            })}
          </div>
          {(feelsRange || windRange || humRange) && (
            <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-around', marginTop: 11, paddingTop: 10, borderTop: '1px solid #f1f4f8' }}>
              {[['feels', feelsRange, '°'], ['wind', windRange ? `${windRange} km/h` : null, ''], ['humidity', humRange, '%']].map(([key, val], idx) => (
                <React.Fragment key={key}>
                  {idx > 0 && <div style={{ width: 1, background: '#eef1f6' }} />}
                  <button
                    onClick={() => setOpenMetric(openMetric === key ? null : key)}
                    style={{
                      textAlign: 'center', flex: 1, border: 'none', cursor: 'pointer', padding: '4px 2px', borderRadius: 8,
                      background: openMetric === key ? '#eef4ff' : 'transparent', fontFamily: 'inherit',
                    }}>
                    <div style={{ fontSize: '0.52rem', color: openMetric === key ? '#2563eb' : '#9aa7bd', fontWeight: 600, letterSpacing: '0.06em' }}>{metricMeta[key].label}</div>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.82rem', color: openMetric === key ? '#1E3A6E' : '#334155' }}>{val || '—'}</div>
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}
          {openMetric && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #eef1f6' }}>
              <div style={{ fontSize: '0.55rem', color: '#9aa7bd', fontWeight: 600, letterSpacing: '0.06em', marginBottom: 6 }}>
                {metricMeta[openMetric].label} BY HOUR
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, justifyContent: 'center' }}>
                {wx.hourly.map((h, i) => {
                  const v = h[metricMeta[openMetric].key];
                  return (
                    <div key={i} style={{ width: 48, textAlign: 'center', background: '#f8fafc', border: '1px solid #eef1f6', borderRadius: 8, padding: '5px 3px' }}>
                      <div style={{ fontSize: '0.52rem', color: '#94a3b8', fontWeight: 600 }}>{wxHourLabel(h.hour)}</div>
                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.78rem', color: '#1E3A6E' }}>{v != null ? `${v}${metricMeta[openMetric].suffix}` : '—'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// `publicMode` renders this exact dashboard at /club for anonymous visitors.
// Same hero, same tile grid, same styles — the member-only parts are simply
// not rendered, and the tiles point at their /club equivalents. Rendering the
// real component (rather than a lookalike) is what keeps the two in step.
export default function DashboardPage({ publicMode = false }) {
  const { member } = useAuth();
  // Scorer role gets a stripped-down dashboard — no fees, no notices, no
  // rankings. Just the next/active match and a Setup button. We dispatch
  // here so neither sub-component breaks the Rules of Hooks.
  if (!publicMode && member?.role === 'scorer') return <ScorerDashboard member={member} />;
  return <MemberOrAdminDashboard publicMode={publicMode} />;
}

function MemberOrAdminDashboard({ publicMode = false }) {
  const { member, isObserver, isAdmin } = useAuth();
  const [data, setData] = useState({ upcomingMatch:null, myAttendance:null, myFees:null, myRank:null, notices:[], unreadNoticeCount:0, activeSeries:null, loading:true });
  const [wheelData, setWheelData] = useState(null);
  const [justRevealed, setJustRevealed] = useState(false);  // triggers animation

  // ── Leave applications (Part B) ──────────────────────────
  const [leaveOpen, setLeaveOpen] = useState(false);       // modal open
  const [myLeaves, setMyLeaves] = useState([]);            // my applications
  function refreshMyLeaves() {
    api.get('/leave/mine').then(r => setMyLeaves(r.data?.applications || [])).catch(() => {});
  }
  useEffect(() => { if (!isObserver && !publicMode) refreshMyLeaves(); }, [isObserver, publicMode]);

  // Wrap card in Link (admins → /admin/captaincy) or plain div (members → no nav)
  const CardWrap = ({ children }) => isAdmin
    ? <Link to="/admin/captaincy" style={{ textDecoration: 'none' }}>{children}</Link>
    : <div>{children}</div>;

  useEffect(() => {
    // Every call below is member-scoped (my attendance, my fees, my rank, my
    // notices). A public visitor has none of these, so skip the round trip
    // entirely rather than firing five requests that will 401.
    if (publicMode) {
      // The club page has no session, so it asks the public endpoint — the
      // stream is on the club's public channel either way.
      api.get('/public/live-now')
        .then(r => setData(d => ({ ...d, live: r.data.live || null, loading: false })))
        .catch(() => setData(d => ({ ...d, loading: false })));
      return;
    }
    Promise.allSettled([
      api.get('/attendance/my?year=2026'),
      api.get('/fees/my'),
      api.get('/leaderboards/points'),
      api.get('/notices?limit=3'),
      api.get('/series/active'),
      // Empty for all but the two captains, so it costs them nothing.
      api.get('/series/my-captain-fixtures').catch(() => ({ data: { fixtures: [] } })),
      api.get('/matches/live-now').catch(() => ({ data: { live: null } })),
    ]).then(([attR, feeR, rankR, notR, serR, capR, liveR]) => {
      const att = attR.status==='fulfilled' ? attR.value.data : null;
      const fee = feeR.status==='fulfilled' ? feeR.value.data : null;
      const points = rankR.status==='fulfilled' ? (rankR.value.data.points||[]) : [];
      const rankIdx = points.findIndex(r=>r.id===member?.id);
      const rank = rankIdx >= 0 ? { ...points[rankIdx], rank: rankIdx + 1 } : null;
      const notices = notR.status==='fulfilled' ? notR.value.data.notices||[] : [];
      const unreadNoticeCount = notR.status==='fulfilled' ? (notR.value.data.unreadCount || 0) : 0;
      const series = serR.status==='fulfilled' ? serR.value.data.series : null;
      const captainFixtures = capR.status==='fulfilled' ? (capR.value.data.fixtures || []) : [];
      const live = liveR.status==='fulfilled' ? (liveR.value.data.live || null) : null;
      setData({ myAttendance:att?.summary, myFees:fee, myRank:rank, notices, unreadNoticeCount, activeSeries:series, captainFixtures, live, loading:false });
    });

    // ─── Captain Wheel: initial load + live polling during wheel window ───
    const SEEN_KEY = 'ucc_wheel_seen_match_v1';

    function fireReveal() {
      // Simple confetti — same idea as admin wheel
      const canvas = document.createElement('canvas');
      canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999';
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      document.body.appendChild(canvas);
      const ctx = canvas.getContext('2d');
      const colors = ['#F5C518','#1E3A6E','#dc2626','#16a34a','#7c3aed','#2563eb','#f97316'];
      const pieces = [];
      const cx = canvas.width/2, cy = canvas.height/3;
      for (let i=0;i<120;i++) {
        const a = Math.random()*Math.PI*2, s = 4 + Math.random()*7;
        pieces.push({ x:cx,y:cy,vx:Math.cos(a)*s,vy:Math.sin(a)*s-3,w:6+Math.random()*6,h:10+Math.random()*8,rot:Math.random()*6.28,vrot:(Math.random()-0.5)*0.3,color:colors[Math.floor(Math.random()*colors.length)],life:0 });
      }
      const start = performance.now();
      function frame(now) {
        if (now-start > 2800) { document.body.removeChild(canvas); return; }
        ctx.clearRect(0,0,canvas.width,canvas.height);
        for (const p of pieces) {
          p.vy += 0.22; p.x+=p.vx; p.y+=p.vy; p.rot+=p.vrot; p.life++;
          const alpha = Math.max(0, 1 - p.life/130);
          ctx.save(); ctx.globalAlpha=alpha; ctx.translate(p.x,p.y); ctx.rotate(p.rot);
          ctx.fillStyle = p.color; ctx.fillRect(-p.w/2,-p.h/2,p.w,p.h);
          ctx.restore();
        }
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    let pollTimer = null;
    let prevRecordKey = null;

    function loadWheel(isInitial) {
      if (publicMode) return;
      api.get('/captaincy/wheel-candidates').then(r => {
        const d = r.data;
        const rec = d?.existing_record;
        const matchId = d?.match?.id;
        const recordKey = rec && matchId ? `${matchId}:${rec.capt1_id}:${rec.capt2_id}` : null;

        // Detect NEW reveal: record appeared where there was none before in this session
        if (!isInitial && recordKey && recordKey !== prevRecordKey) {
          const seen = sessionStorage.getItem(SEEN_KEY);
          if (seen !== recordKey) {
            sessionStorage.setItem(SEEN_KEY, recordKey);
            setJustRevealed(true);
            fireReveal();
            // Keep animation flag on for 3s
            setTimeout(() => setJustRevealed(false), 3000);
          }
        }
        prevRecordKey = recordKey;
        setWheelData(d);

        // Decide whether to keep polling
        if (d?.wheel_window?.open && !rec) {
          // Window open, no captains yet — poll every 30s
          pollTimer = setTimeout(() => loadWheel(false), 30000);
        } else if (d?.wheel_window?.open && rec) {
          // Captains just confirmed, still in window — slow poll (90s) in case they re-spin
          pollTimer = setTimeout(() => loadWheel(false), 90000);
        }
        // Otherwise no polling
      }).catch(() => {});
    }
    loadWheel(true);

    return () => {
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [member?.id]);

  const d = new Date();
  const dateStr = `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  const firstName = member?.display_name?.split(' ')[0] || 'Player';
  const dues = data.myFees?.totalDue || 0;
  const att = data.myAttendance?.percentage || 0;
  const rank = data.myRank?.rank || '—';
  const pts = data.myRank ? Math.round(parseFloat(data.myRank.total_pts||0)) : 0;
  const series = data.activeSeries;

  if (data.loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}><div style={{ width:32, height:32, border:'3px solid #e5e7eb', borderTopColor:'#1E3A6E', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} /></div>;

  return (
    <div style={S.page}>
      <style>{`@keyframes uccPulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.15); opacity: 0.75; } }`}</style>
      <CelebrationOverlay />

      {/* Hero */}
      <div style={S.hero}>
        <div style={S.heroWatermark}>UCC</div>
        <div style={S.heroDate}>{publicMode ? 'WELCOME TO' : dateStr}</div>
        <div style={S.heroTitle}>{publicMode ? 'United Cricket Club' : `Hey ${firstName}! 🏏`}</div>
        <div style={S.heroSub}>{publicMode ? 'Ludhiana · Since 2004' : 'Season 2026'}</div>
      </div>

      <div style={S.body}>

        {/* Ground weather for the next match */}
        <WeatherCard />

        {/* Guest welcome — public only. Sets expectations about what's here
            and where the member side begins, so a visitor isn't left guessing
            why some things are missing. */}
        {publicMode && (
          <div style={{
            background:'white', borderRadius:14, padding:'16px 18px', marginTop:16,
            border:'1px solid #f1f5f9', borderLeft:'4px solid #F5C518',
          }}>
            <div style={{ fontSize:'0.88rem', color:'#4b5563', lineHeight:1.8 }}>
              We started in 2004 with a few friends and a free Sunday morning.
              What began as a weekly game in Ludhiana has since taken us to the
              Test grounds at Dharamshala and Mohali, the international stadium
              at Dehradun, and across to the UAE — the ICC Academy, Dubai Sevens,
              Karwan Sports Club in Ajman and, in September 2026, the Sharjah
              Cricket Stadium, the ground that has hosted more one-day
              internationals than any other. Tour after tour, at home and
              abroad, with more still to come.
              <br /><br />
              Twenty-two seasons on, the cricket is played hard and the company
              is still the reason we turn up.
            </div>
            <div style={{ fontSize:'0.8rem', color:'#9ca3af', marginTop:14 }}>
              For rankings, records and scorecards, tap the tiles below.
            </div>
          </div>
        )}

        {/* Active Series */}
        {/* Live stream.
            Before the day: a slim banner — a player that cannot play yet does
            not deserve half the screen. On the day: the stream itself, in
            place, because that is the point. */}
        {data.live?.live_video_id && !data.live.is_today && (
          <a href={`https://www.youtube.com/watch?v=${data.live.live_video_id}`}
             target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none' }}>
            <div style={{ background:'#111827', borderRadius:12, padding:'10px 14px', marginBottom:12,
              display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ background:'#374151', color:'white', fontSize:'0.58rem', fontWeight:900,
                padding:'3px 7px', borderRadius:4, letterSpacing:'0.08em', flexShrink:0 }}>LIVE SOON</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:800,
                  fontSize:'0.85rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {data.live.capt1_team && data.live.capt2_team
                    ? `${data.live.capt1_team} v ${data.live.capt2_team}`
                    : 'UCC live'}
                </div>
                <div style={{ color:'rgba(255,255,255,0.55)', fontSize:'0.68rem' }}>
                  {data.live.venue || 'UCC Ground'}
                  {data.live.match_date
                    ? ` · ${new Date(data.live.match_date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}`
                    : ''}
                </div>
              </div>
              <span style={{ color:'#F5C518', fontSize:'0.72rem', fontWeight:700, flexShrink:0 }}>Watch ↗</span>
            </div>
          </a>
        )}

        {data.live?.live_video_id && data.live.is_today && (
          <div style={{ background:'#111827', borderRadius:14, overflow:'hidden', marginBottom:12 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px' }}>
              <span style={{ background:'#dc2626', color:'white', fontSize:'0.62rem', fontWeight:900,
                padding:'3px 8px', borderRadius:4, letterSpacing:'0.08em' }}>● LIVE</span>
              <div style={{ color:'white', fontFamily:'Rajdhani,sans-serif', fontWeight:800, fontSize:'0.95rem' }}>
                {data.live.capt1_team && data.live.capt2_team
                  ? `${data.live.capt1_team} v ${data.live.capt2_team}`
                  : 'UCC live'}
              </div>
            </div>
            <div style={{ position:'relative', width:'100%', paddingTop:'56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${data.live.live_video_id}?rel=0&playsinline=1`}
                title="UCC live"
                allow="accelerometer; autoplay; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', border:'none' }}
              />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 14px' }}>
              <span style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.72rem' }}>
                {data.live.venue || 'UCC Ground'}
              </span>
              <a href={`https://www.youtube.com/watch?v=${data.live.live_video_id}`}
                 target="_blank" rel="noopener noreferrer"
                 style={{ color:'#F5C518', fontSize:'0.75rem', fontWeight:700, textDecoration:'none' }}>
                Watch on YouTube ↗
              </a>
            </div>
          </div>
        )}

        {/* A captain with an XI still to name gets told so, rather than
            having to know the Series page exists. */}
        {!publicMode && (data.captainFixtures || []).length > 0 && (
          <Link to="/series" style={{ textDecoration:'none' }}>
            <div style={{ background:'#F5C518', borderRadius:14, padding:'14px 16px', marginBottom:12,
              display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
              <div>
                <div style={{ fontSize:'0.68rem', fontWeight:800, letterSpacing:'0.08em', color:'#7c5e00' }}>
                  🧢 YOU ARE CAPTAIN
                </div>
                {(data.captainFixtures || []).slice(0, 2).map(f => (
                  <div key={f.id} style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:800, color:'#1E3A6E', marginTop:3 }}>
                    {f.picked > 0 ? '✓ XI named' : 'Pick your XI'} — {f.series_name}
                    <span style={{ fontWeight:600, fontSize:'0.8rem' }}>
                      {' · '}{f.match_date ? new Date(f.match_date).toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'}) : ''}
                      {f.venue ? ` · ${f.venue}` : ''}
                    </span>
                  </div>
                ))}
              </div>
              <span style={{ color:'#1E3A6E', fontWeight:800 }}>›</span>
            </div>
          </Link>
        )}

        {series && !publicMode && (
          <Link to="/series" style={{ textDecoration:'none' }}>
            <div style={{ background:'#1E3A6E', borderRadius:14, padding:'14px 16px', marginTop:16, boxShadow:'0 4px 16px rgba(30,58,110,0.2)' }}>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'#F5C518', marginBottom:4 }}>🏅 Active Series</div>
              <div style={{ fontFamily:'Rajdhani,sans-serif', fontSize:'1.3rem', fontWeight:700, color:'white', marginBottom:3 }}>{series.name}</div>
              <div style={{ fontSize:'0.78rem', color:'rgba(255,255,255,0.55)', marginBottom: series.capt_a||series.capt_b ? 12 : 0 }}>
                {series.innings_per_match>1?'Double Innings':'Bilateral'}{series.overs_per_innings?` · ${series.overs_per_innings} overs`:''}
              </div>
              {(series.capt_a||series.capt_b) && (
                <div style={{ display:'flex', borderTop:'1px solid rgba(255,255,255,0.12)', paddingTop:10 }}>
                  <div style={{ flex:1, textAlign:'center' }}>
                    <div style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.4)', marginBottom:2 }}>Captain A</div>
                    <div style={{ fontFamily:'Rajdhani,sans-serif', fontSize:'1rem', fontWeight:700, color:'white' }}>{series.capt_a||'—'}</div>
                  </div>
                  <div style={{ width:1, background:'rgba(255,255,255,0.12)' }} />
                  <div style={{ flex:1, textAlign:'center' }}>
                    <div style={{ fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.4)', marginBottom:2 }}>Captain B</div>
                    <div style={{ fontFamily:'Rajdhani,sans-serif', fontSize:'1rem', fontWeight:700, color:'white' }}>{series.capt_b||'—'}</div>
                  </div>
                </div>
              )}
            </div>
          </Link>
        )}

        {/* Season Stats — personal figures, so member-only */}
        {!isObserver && !publicMode && <>
          <div style={{ ...S.row, marginTop:20, marginBottom:10 }}>
            <div style={S.sectionLabel}>My Season 2026</div>
            <Link to={`/stats-v2/${member?.id}`} style={{ fontSize:'0.78rem', color:'#1E3A6E', fontWeight:600, textDecoration:'none' }}>Full Stats →</Link>
          </div>
          <div style={S.grid2}>
            <Link to="/attendance" style={{ textDecoration:'none' }}>
              <div style={{ ...S.statBox, cursor:'pointer', borderBottom:'3px solid ' + (att<50?'#dc2626':'#1E3A6E') }}>
                <div style={{ ...S.statVal, color: att<50?'#dc2626':'#1E3A6E' }}>{att}%</div>
                <div style={S.statLbl}>Attendance</div>
                {att<50 && <div style={{ fontSize:'0.68rem', color:'#dc2626', marginTop:3 }}>⚠ Below 50%</div>}
              </div>
            </Link>
            <Link to="/fees" style={{ textDecoration:'none' }}>
              <div style={{ ...S.statBox, cursor:'pointer', borderBottom:'3px solid ' + (dues>0?'#dc2626':'#16a34a') }}>
                {dues>0
                  ? <><div style={{ ...S.statVal, color:'#dc2626', fontSize:'1.3rem' }}>₹{Math.round(dues).toLocaleString('en-IN')}</div><div style={S.statLbl}>Dues</div></>
                  : <><div style={{ ...S.statVal, color:'#16a34a', fontSize:'1.8rem' }}>✓</div><div style={S.statLbl}>All Clear</div></>
                }
              </div>
            </Link>
            <Link to="/leaderboards" style={{ textDecoration:'none', gridColumn:'span 2' }}>
              <div style={{ ...S.statBox, cursor:'pointer', borderBottom:'3px solid #F5C518', display:'flex', justifyContent:'space-around', alignItems:'center' }}>
                <div style={{ textAlign:'center' }}>
                  <div style={S.statVal}>#{rank}</div>
                  <div style={S.statLbl}>UCC Rank</div>
                </div>
                <div style={{ width:1, height:36, background:'#e2e8f0' }} />
                <div style={{ textAlign:'center' }}>
                  <div style={S.statVal}>{pts}</div>
                  <div style={S.statLbl}>Points</div>
                </div>
              </div>
            </Link>
          </div>
        </>}

        {/* Captain Wheel v3 — shows during wheel window OR when captains already confirmed */}
        {/* Inline keyframes for reveal animation */}
        <style>{`
          @keyframes ucc-reveal-slide {
            0%   { opacity: 0; transform: translateY(24px) scale(0.95); }
            60%  { opacity: 1; transform: translateY(0) scale(1.04); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes ucc-pulse {
            0%, 100% { box-shadow: 0 4px 16px rgba(30,58,110,0.25); }
            50%      { box-shadow: 0 6px 28px rgba(245,197,24,0.65); }
          }
          @keyframes ucc-wheel-opens {
            0%,100% { opacity:1; } 50% { opacity:0.5; }
          }
        `}</style>
        {!publicMode && wheelData && wheelData.match && (wheelData.existing_record || wheelData.wheel_window?.open) && (
          <>
            {/* CASE 1: Captains already confirmed */}
            {wheelData.existing_record && (
              <CardWrap>
                <div style={{
                  background: 'linear-gradient(135deg, #1E3A6E, #2563eb)',
                  borderRadius: 14, padding: '14px 16px', marginTop: 16,
                  boxShadow: '0 4px 16px rgba(30,58,110,0.25)', color: 'white',
                  animation: justRevealed
                    ? 'ucc-reveal-slide 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both, ucc-pulse 1.2s ease-in-out 0.8s 2'
                    : 'none',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F5C518' }}>
                      {justRevealed ? '🎲 Just Drawn · Sunday' : '🎖️ Captains · Sunday'}
                    </div>
                    <div style={{ fontSize: '0.66rem', opacity: 0.7 }}>
                      {new Date(wheelData.match.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: '0.6rem', opacity: 0.6, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>Captain A</div>
                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem' }}>{wheelData.existing_record.capt1_name || '—'}</div>
                    </div>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 900, fontSize: '1rem', color: '#F5C518', padding: '0 8px' }}>VS</div>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: '0.6rem', opacity: 0.6, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>Captain B</div>
                      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem' }}>{wheelData.existing_record.capt2_name || '—'}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.66rem', opacity: 0.65, marginTop: 8, textAlign: 'center' }}>
                    {wheelData.existing_record.selection_method === 'wagon_wheel' ? '🎲 Drawn by wheel' : '✍️ Set manually'}
                  </div>
                  {(member?.id === wheelData.existing_record.capt1_id || member?.id === wheelData.existing_record.capt2_id) ? (
                    <Link
                      to={`/matches/${wheelData.match.id}/setup`}
                      style={{
                        display: 'block', textAlign: 'center', marginTop: 12,
                        padding: '10px 12px', background: '#F5C518', color: '#1E3A6E',
                        borderRadius: 8, textDecoration: 'none',
                        fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.85rem',
                      }}
                    >
                      ⚙️ Set up your squad
                    </Link>
                  ) : !isAdmin ? (
                    <Link
                      to={`/matches/${wheelData.match.id}/setup`}
                      style={{
                        display: 'block', textAlign: 'center', marginTop: 12,
                        padding: '10px 12px', background: 'rgba(255,255,255,0.12)', color: '#fff',
                        borderRadius: 8, textDecoration: 'none',
                        fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.8rem',
                      }}
                    >
                      👀 View squad
                    </Link>
                  ) : null}
                </div>
              </CardWrap>
            )}

            {/* CASE 2: Wheel window is OPEN but captains not yet set */}
            {!wheelData.existing_record && wheelData.wheel_window?.open && (wheelData.continuing_captain || wheelData.candidates?.length > 0) && (
              <CardWrap>
                <div style={{
                  background: 'white', border: '2px solid #F5C518', borderRadius: 14,
                  padding: '14px 16px', marginTop: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  cursor: isAdmin ? 'pointer' : 'default', animation: 'ucc-wheel-opens 2s ease-in-out infinite',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#dc2626' }}>🟢 Wheel is LIVE</div>
                    <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>
                      {new Date(wheelData.match.match_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>

                  {wheelData.continuing_captain && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 12px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '1rem' }}>🏆</span>
                      <div>
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#92400e', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Continuing</div>
                        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: '#1E3A6E' }}>{wheelData.continuing_captain.display_name}</div>
                      </div>
                    </div>
                  )}

                  {wheelData.candidates?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                        Pool ({wheelData.candidates.length} eligible{wheelData.availability_locked ? ', available' : ''})
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {wheelData.candidates.slice(0, 10).map(c => (
                          <div key={c.member_id} style={{ background: '#f1f5f9', borderRadius: 20, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#1E3A6E' }}>
                            {c.display_name}
                          </div>
                        ))}
                        {wheelData.candidates.length > 10 && (
                          <div style={{ background: '#f1f5f9', borderRadius: 20, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>
                            +{wheelData.candidates.length - 10}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!wheelData.availability_locked && wheelData.candidates?.length > 0 && (
                    <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 8 }}>
                      ⚡ Updates after Friday availability closes
                    </div>
                  )}

                  <div style={{
                    marginTop: 10, padding: '8px 12px', background: '#1E3A6E', borderRadius: 8,
                    textAlign: 'center', color: 'white', fontFamily: 'Rajdhani,sans-serif',
                    fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>
                    {isAdmin ? '🎲 Go Spin the Wheel' : '🎲 Watch the draw live'}
                  </div>
                </div>
              </CardWrap>
            )}
          </>
        )}


        {/* Quick Actions */}
        <div style={{ ...S.sectionLabel, marginTop:20, marginBottom:10 }}>Quick Actions</div>

        {/* Ask UCC — full-width animated hero chip (the headline feature) */}
        <style>{`
          @keyframes uccAskShimmer {
            0%   { background-position: 0% 50%; }
            50%  { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes uccAskSparkle {
            0%, 100% { transform: scale(1) rotate(0deg);   opacity: 1; }
            50%      { transform: scale(1.15) rotate(8deg); opacity: 0.85; }
          }
          .ucc-ask-chip {
            position: relative;
            display: block;
            text-decoration: none;
            margin-bottom: 10px;
            border-radius: 14px;
            overflow: hidden;
            background: linear-gradient(120deg, #1E3A6E 0%, #3a5fb0 35%, #6d7fff 50%, #3a5fb0 65%, #1E3A6E 100%);
            background-size: 200% 200%;
            animation: uccAskShimmer 6s ease-in-out infinite;
            box-shadow: 0 4px 14px rgba(30, 58, 110, 0.28);
            transition: transform 120ms ease, box-shadow 120ms ease;
            cursor: pointer;
          }
          .ucc-ask-chip:active { transform: scale(0.985); box-shadow: 0 2px 8px rgba(30, 58, 110, 0.22); }
          .ucc-ask-chip:hover  { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(30, 58, 110, 0.35); }
          .ucc-ask-sparkle {
            display: inline-block;
            animation: uccAskSparkle 2.2s ease-in-out infinite;
            transform-origin: center;
          }
        `}</style>
        <Link to={publicMode ? '/club/ask' : '/ask'} className="ucc-ask-chip" aria-label="Ask UCC — AI cricket assistant">
          <div style={{
            display:'flex', alignItems:'center', gap:14,
            padding:'16px 18px',
            color:'white',
          }}>
            {/* Sparkle icon block */}
            <div style={{
              width:44, height:44, flexShrink:0,
              borderRadius:'50%',
              background:'rgba(255,255,255,0.15)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:'1.5rem',
              boxShadow:'inset 0 0 0 1px rgba(255,255,255,0.2)',
            }}>
              <span className="ucc-ask-sparkle">✨</span>
            </div>
            {/* Text block */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{
                fontFamily:'Rajdhani,sans-serif',
                fontWeight:800, fontSize:'1.05rem',
                letterSpacing:'0.05em',
                lineHeight:1.1,
                display:'flex', alignItems:'center', gap:8,
              }}>
                ASK UCC
                <span style={{
                  fontSize:'0.6rem',
                  background:'rgba(255,255,255,0.22)',
                  padding:'2px 7px',
                  borderRadius:8,
                  fontWeight:700,
                  letterSpacing:'0.08em',
                }}>BETA</span>
              </div>
              <div style={{
                fontSize:'0.78rem',
                color:'rgba(255,255,255,0.85)',
                marginTop:3,
                fontWeight:500,
              }}>
                Your cricket assistant — runs, wickets, captains, partnerships
              </div>
            </div>
            {/* Chevron */}
            <div style={{
              fontSize:'1.4rem',
              color:'rgba(255,255,255,0.9)',
              fontWeight:300,
              marginLeft:4,
            }}>›</div>
          </div>
        </Link>

        {(() => {
          // Public visitors get the club-wide tiles only, pointed at their /club
          // equivalents. Everything omitted is member business: availability,
          // check-in, payments, floaters, personal performance, notices, leave.
          const tiles = publicMode ? [
            { icon:'👥', label:'Members',     to:'/club/members' },
            { icon:'🏆', label:'Rankings',    to:'/club/rankings' },
            { icon:'📋', label:'Scorecards',  to:'/club/scorecards' },
            { icon:'🏆', label:'Records',     to:'/club/records' },
            { icon:'⚔️', label:'Head to Head', to:'/club/compare' },
            { icon:'🍌', label:'Banana Awards', to:'/club/banana' },
            { icon:'📊', label:'Performance', to:'/club/performance' },
            { icon:'🎥', label:'Match Videos', to:'/club/videos' },
          ] : [
            { icon:'🏆', label:'Records',     to:'/records' },
            { icon:'⚔️', label:'Head to Head', to:'/compare-v2' },
            { icon:'🍌', label:'Banana Awards', to:'/banana-v2' },
            { icon:'✋', label:'Availability', to:'/availability' },
            { icon:'📍', label:'Check In',    to:'/checkin' },
            ...(!isObserver ? [{ icon:'💳', label:'Pay', to:'/payment' }] : []),
            { icon:'🔄', label:'Floaters',    to:'/floaters' },
            { icon:'📊', label:'Performance', to:'/performance' },
            { icon:'📋', label:'Scorecards',  to:'/scorecards' },
            { icon:'🎥', label:'Match Videos', to:'/videos' },
            { icon:'🔔', label:'Notices',     to:'/notices', badge: data.unreadNoticeCount },
            ...(!isObserver ? [{ icon:'🏖️', label:'Apply Leave', onClick: () => setLeaveOpen(true) }] : []),
          ];
          const remainder = tiles.length % 3;
          const mainTiles = remainder > 0 ? tiles.slice(0, tiles.length - remainder) : tiles;
          const lastTiles = remainder > 0 ? tiles.slice(tiles.length - remainder) : [];

          const renderTile = (a) => {
            const inner = (
              <div style={{ ...S.actionTile, width: '100%', position: 'relative' }}>
                <span style={S.actionIcon}>{a.icon}</span>
                <span style={S.actionLbl}>{a.label}</span>
                {a.badge > 0 && (
                  <span style={{
                    position:'absolute', top:8, right:10,
                    minWidth:20, height:20, padding:'0 6px',
                    borderRadius:10, background:'#dc2626', color:'white',
                    fontSize:'0.7rem', fontWeight:800,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow:'0 0 0 2px white',
                    animation:'uccPulse 1.2s ease-in-out infinite',
                  }}>{a.badge > 99 ? '99+' : a.badge}</span>
                )}
              </div>
            );
            if (a.onClick) {
              return (
                <div key={a.label} onClick={a.onClick} style={{ flex: 1, cursor: 'pointer' }}>
                  {inner}
                </div>
              );
            }
            return (
              <Link key={a.to} to={a.to} style={{ textDecoration:'none', flex: 1 }}>
                {inner}
              </Link>
            );
          };

          return (
            <>
              <div style={publicMode ? S.grid2 : S.grid3}>
                {mainTiles.map(renderTile)}
              </div>
              {lastTiles.length > 0 && (
                <div style={{ display:'flex', gap:10, marginTop:10 }}>
                  {lastTiles.map(renderTile)}
                </div>
              )}
              {/* UCC Charter — full-width standalone (its historical position).
                  Member-only: the charter sets out club rules, fees and
                  obligations, which is internal business rather than a shop
                  window. */}
              {!publicMode && (
              <Link to="/charter" style={{ textDecoration:'none', display:'block', marginTop: 10 }}>
                <div style={{ ...S.actionTile, width:'100%', position:'relative' }}>
                  <span style={S.actionIcon}>📜</span>
                  <span style={S.actionLbl}>UCC Charter</span>
                </div>
              </Link>
              )}
            </>
          );
        })()}

        {/* Floater Chart */}

        {/* Inactive member */}
        {isObserver && (
          <div style={{ ...S.card, marginTop:20, padding:24, textAlign:'center' }}>
            <div style={{ fontSize:'3rem', marginBottom:12 }}>🏏</div>
            <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.1rem', color:'#1E3A6E', marginBottom:6 }}>Welcome back, {firstName}!</div>
            <div style={{ fontSize:'0.85rem', color:'#6b7280', marginBottom:16 }}>View scorecards, leaderboards and rankings.</div>
            <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
              <Link to="/leaderboards" style={{ padding:'8px 16px', background:'#1E3A6E', color:'white', borderRadius:8, fontSize:'0.85rem', fontFamily:'Rajdhani,sans-serif', fontWeight:700, textDecoration:'none' }}>Rankings</Link>
              <Link to="/scorecards" style={{ padding:'8px 16px', border:'2px solid #1E3A6E', color:'#1E3A6E', borderRadius:8, fontSize:'0.85rem', fontFamily:'Rajdhani,sans-serif', fontWeight:700, textDecoration:'none' }}>Scorecards</Link>
            </div>
          </div>
        )}

      </div>
      <div style={S.spacer(24)} />

      {leaveOpen && !publicMode && (
        <LeaveModal
          myLeaves={myLeaves}
          onClose={() => setLeaveOpen(false)}
          onSubmitted={() => { refreshMyLeaves(); }}
          onWithdrawn={() => { refreshMyLeaves(); }}
        />
      )}
    </div>
  );
}

// Format an ISO date (YYYY-MM-DD or full ISO) as "12 Jun"
function fmtLeaveDate(d) {
  if (!d) return '';
  const dt = new Date(typeof d === 'string' && d.length === 10 ? d + 'T00:00:00Z' : d);
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

// ─── LeaveModal ───────────────────────────────────────────
// Member applies for leave: date range + reason. The backend enforces the
// "before Friday availability message" rule and returns a clear error if any
// Sunday in the range is already past its window.
function LeaveModal({ myLeaves = [], onClose, onSubmitted, onWithdrawn }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Min selectable date = today (no past leave)
  const todayISO = new Date().toISOString().slice(0, 10);

  async function submit() {
    if (!startDate || !endDate) { toast.error('Pick a start and end date'); return; }
    if (endDate < startDate) { toast.error('End date cannot be before start date'); return; }
    if (!reason.trim()) { toast.error('Please give a reason'); return; }
    setSaving(true);
    try {
      await api.post('/leave', { start_date: startDate, end_date: endDate, reason: reason.trim() });
      toast.success('Leave application submitted');
      setStartDate(''); setEndDate(''); setReason('');
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  }

  async function withdraw(id) {
    if (!window.confirm('Withdraw this leave application?')) return;
    try {
      await api.delete(`/leave/${id}`);
      toast.success('Withdrawn');
      onWithdrawn();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to withdraw');
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 400, maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.15rem', color: '#1E3A6E', marginBottom: 4 }}>
          🏖️ Apply for Leave
        </div>
        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 16 }}>
          Apply before the Friday availability message for the Sundays you'll miss.
        </div>

        <label style={leaveLbl}>From</label>
        <input type="date" value={startDate} min={todayISO}
          onChange={(e) => setStartDate(e.target.value)} style={leaveInput} />

        <label style={leaveLbl}>To</label>
        <input type="date" value={endDate} min={startDate || todayISO}
          onChange={(e) => setEndDate(e.target.value)} style={leaveInput} />

        <label style={leaveLbl}>Reason</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Family vacation, out of town"
          rows={3} style={{ ...leaveInput, resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onClose} disabled={saving}
            style={{ background: 'white', border: '1px solid #d1d5db', color: '#374151', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', fontSize: '0.8rem', cursor: 'pointer' }}>
            Close
          </button>
          <button onClick={submit} disabled={saving}
            style={{ background: '#1E3A6E', border: 'none', color: 'white', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', fontSize: '0.8rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Submitting…' : 'Submit'}
          </button>
        </div>

        {/* Existing applications */}
        {myLeaves.length > 0 && (
          <div style={{ marginTop: 18, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
              My Applications
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {myLeaves.map(lv => {
                const color = lv.status === 'approved' ? '#16a34a' : lv.status === 'rejected' ? '#dc2626' : '#b45309';
                const bg    = lv.status === 'approved' ? '#dcfce7' : lv.status === 'rejected' ? '#fee2e2' : '#fef3c7';
                return (
                  <div key={lv.id} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1E3A6E' }}>
                        {fmtLeaveDate(lv.start_date)} → {fmtLeaveDate(lv.end_date)}
                      </div>
                      <span style={{ flexShrink: 0, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color, background: bg, padding: '3px 7px', borderRadius: 5 }}>
                        {lv.status}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>{lv.reason}</div>
                    {lv.status === 'approved' && (
                      <div style={{ fontSize: '0.68rem', color: '#16a34a', marginTop: 3 }}>
                        {[lv.leave_from_reply && 'Leave from reply', lv.leave_from_attendance && `Attendance: ${lv.attendance_treatment}`].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {lv.status === 'rejected' && lv.review_note && (
                      <div style={{ fontSize: '0.68rem', color: '#dc2626', marginTop: 3 }}>Note: {lv.review_note}</div>
                    )}
                    {lv.status === 'pending' && (
                      <button onClick={() => withdraw(lv.id)}
                        style={{ marginTop: 6, background: 'white', border: '1px solid #dc2626', color: '#dc2626', padding: '4px 10px', borderRadius: 5, fontSize: '0.68rem', fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', cursor: 'pointer' }}>
                        Withdraw
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const leaveLbl = { display: 'block', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 };
const leaveInput = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', marginBottom: 12, boxSizing: 'border-box', fontFamily: 'inherit' };
// Stripped-down dashboard for `role='scorer'`. Lists upcoming matches with
// two actions per match: ⚙️ Match Setup and 🟢 Score (if a live innings exists
// or setup is locked, the second button takes them straight to scoring).
function ScorerDashboard({ member }) {
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/matches?year=${new Date().getFullYear()}&upcoming=true&limit=10`)
      .then(r => { if (!cancelled) setMatches(r.data?.matches || []); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Failed to load matches'); });
    return () => { cancelled = true; };
  }, []);

  function fmtDate(d) {
    if (!d) return '';
    const dt = new Date(d);
    return `${DAYS[dt.getDay()]}, ${dt.getDate()} ${MONTHS[dt.getMonth()]}`;
  }

  return (
    <div style={S.page}>
      <div style={S.hero}>
        <div style={S.heroDate}>SCORER ACCESS</div>
        <div style={S.heroTitle}>Hi, {member?.display_name?.split(' ')[0] || 'Scorer'}.</div>
        <div style={S.heroSub}>Pick the match to set up or score.</div>
        <div style={S.heroWatermark}>UCC</div>
      </div>

      <div style={{ ...S.body, paddingTop: 16 }}>
        {error && (
          <div style={{ background: '#fef3c7', color: '#92400e', padding: 14, borderRadius: 8, fontSize: '0.85rem', marginBottom: 12 }}>
            <strong>Couldn't load matches.</strong><br/>
            {error}
          </div>
        )}

        {matches === null && !error && (
          <div className="loading-spinner"><div className="spinner" /></div>
        )}

        {matches && matches.length === 0 && (
          <div style={{ ...S.card, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>🏏</div>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1E3A6E', marginBottom: 6 }}>
              No upcoming matches
            </div>
            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Once a match is scheduled, it'll appear here for you to set up and score.
            </div>
          </div>
        )}

        {matches && matches.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {matches.map(m => {
              const teamA = m.capt1_team || 'Side A';
              const teamB = m.capt2_team || 'Side B';
              const isLive = m.data_source === 'ucc_live';
              const captainsSet = !!(m.capt1_id && m.capt2_id);
              return (
                <div key={m.id} style={{ ...S.card, padding: 14 }}>
                  <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {fmtDate(m.match_date)}{m.series_name ? ` · ${m.series_name}` : ''}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                    <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1E3A6E' }}>
                      {teamA} <span style={{ color: '#9ca3af' }}>vs</span> {teamB}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: isLive ? '#16a34a' : '#9ca3af', fontWeight: 700 }}>
                      {isLive ? '🔴 LIVE MODE' : 'CricHeroes'}
                    </div>
                  </div>

                  {captainsSet ? (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <Link
                        to={`/matches/${m.id}/setup`}
                        style={{
                          flex: 1, textAlign: 'center', padding: '10px 12px',
                          background: '#1E3A6E', color: 'white',
                          borderRadius: 8, textDecoration: 'none',
                          fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem',
                        }}
                      >
                        ⚙️ Match Setup
                      </Link>
                      <Link
                        to={`/matches/${m.id}/score`}
                        style={{
                          flex: 1, textAlign: 'center', padding: '10px 12px',
                          border: '2px solid #1E3A6E', color: '#1E3A6E',
                          borderRadius: 8, textDecoration: 'none',
                          fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.85rem',
                        }}
                      >
                        🟢 Score
                      </Link>
                    </div>
                  ) : (
                    <div style={{
                      marginTop: 12, padding: '10px 12px',
                      background: '#f3f4f6', borderRadius: 8,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <span style={{ fontSize: '1rem' }}>🔒</span>
                      <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 600 }}>
                        Captains not set — waiting for admin
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={S.spacer(24)} />
        <div style={{ fontSize: '0.7rem', color: '#9ca3af', textAlign: 'center', padding: '8px 12px' }}>
          You're signed in as a Scorer. To pass the device to the next scorer, use the menu → Sign Out.
        </div>
      </div>
      <div style={S.spacer(24)} />
    </div>
  );
}
